import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ValidatorForm } from "components";
import { TextValidator } from "react-material-ui-form-validator";
import {
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  InputAdornment,
  Container,
  Link,
  Grid,
  Stack,
  Divider,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import LockResetOutlinedIcon from "@mui/icons-material/LockResetOutlined";
import axios from "axios";
import { getEnv } from "../../envConfig";

const env = getEnv();

const MotionBox = motion(Box);
const MotionCard = motion(Card);

const BRAND = "#F9735B";
const BRAND_DARK = "#DC5F49";
const INK = "#0F172A";
const MUTED = "#475569";
const BORDER = "#D9E2EC";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${env.ENDPOINT}/api/restaurant/auth/forgot-password`,
        { email },
      );

      if (response.data.success) {
        setSubmitted(true);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to send reset link. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 2.2, md: 3.2 },
        background: "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
        display: "flex",
        alignItems: "center",
      }}
    >
      <Container maxWidth="xl">
        <MotionBox
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <MotionCard
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            sx={{
              borderRadius: "16px",
              border: `1px solid ${BORDER}`,
              boxShadow: "0 18px 48px rgba(15,23,42,0.09)",
              overflow: "hidden",
              background: "#fff",
              maxWidth: "1100px",
              mx: "auto",
            }}
          >
            <Grid container>
              <Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex" }}>
                <Box
                  sx={{
                    minHeight: { xs: 230, md: 520 },
                    height: "100%",
                    width: "100%",
                    px: { xs: 2.2, md: 3.8 },
                    py: { xs: 2.6, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: { xs: "flex-start", md: "center" },
                    textAlign: { xs: "left", md: "center" },
                    borderRight: {
                      xs: "none",
                      md: "1px solid rgba(255,255,255,0.08)",
                    },
                    background:
                      "radial-gradient(circle at 15% 20%, rgba(56,189,248,0.18), transparent 45%), radial-gradient(circle at 88% 85%, rgba(79,70,229,0.18), transparent 45%), linear-gradient(160deg, #0B1532 0%, #182745 100%)",
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/logo.png"
                    alt="Menux"
                    sx={{ height: { xs: 38, md: 54 }, width: "auto", mb: 1.2 }}
                  />
                  <Typography
                    sx={{
                      fontFamily: '"exo", "Inter", sans-serif',
                      color: "#F8FAFC",
                      fontSize: { xs: "1.5rem", md: "2rem" },
                      fontWeight: 700,
                      lineHeight: 1.15,
                    }}
                  >
                    Reset Password
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1.2,
                      maxWidth: 330,
                      color: "rgba(248,250,252,0.78)",
                      fontFamily: '"Inter", sans-serif',
                      fontSize: { xs: "0.88rem", md: "0.96rem" },
                      lineHeight: 1.7,
                    }}
                  >
                    We will send secure reset instructions to your email so you can access your dashboard quickly.
                  </Typography>
                  <Stack direction="row" spacing={0.8} mt={2.1} flexWrap="wrap" useFlexGap>
                    <Chip label="Secure Link" size="small" sx={{ bgcolor: "rgba(248,250,252,0.12)", color: "#fff", fontWeight: 700 }} />
                    <Chip label="1 Hour Expiry" size="small" sx={{ bgcolor: "rgba(248,250,252,0.12)", color: "#fff", fontWeight: 700 }} />
                    <Chip label="Fast Recovery" size="small" sx={{ bgcolor: "rgba(248,250,252,0.12)", color: "#fff", fontWeight: 700 }} />
                  </Stack>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 7 }} sx={{ display: "flex" }}>
                <Box
                  sx={{
                    height: "100%",
                    width: "100%",
                    minHeight: { xs: "auto", md: 520 },
                    background: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      px: { xs: 2, md: 2.4 },
                      py: { xs: 1.4, md: 1.65 },
                      borderBottom: "1px solid #E2E8F0",
                      background: "#F8FAFC",
                    }}
                  >
                    <Stack direction="row" spacing={1.1} alignItems="center">
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "8px",
                          background: "rgba(249,115,91,0.14)",
                          border: "1px solid rgba(249,115,91,0.25)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: BRAND,
                        }}
                      >
                        {submitted ? (
                          <MarkEmailReadOutlinedIcon sx={{ fontSize: 15 }} />
                        ) : (
                          <LockResetOutlinedIcon sx={{ fontSize: 15 }} />
                        )}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: '"exo", "Inter", sans-serif',
                            fontSize: { xs: "1rem", md: "1.15rem" },
                            fontWeight: 700,
                            color: INK,
                            lineHeight: 1.2,
                          }}
                        >
                          {submitted
                            ? "Check your email"
                            : "Forgot your password?"}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: "0.79rem",
                            color: MUTED,
                          }}
                        >
                          {submitted
                            ? "If your account exists, reset instructions were sent."
                            : "Enter your email and we will send reset instructions."}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  <CardContent
                    sx={{
                      p: { xs: 2, sm: 2.2, md: 2.2 },
                      flex: 1,
                      display: "flex",
                      alignItems: { xs: "flex-start", md: "center" },
                    }}
                  >
                    <Box sx={{ width: "100%", maxWidth: 620, mx: "auto" }}>
                      {submitted ? (
                        <Box sx={{ textAlign: "center" }}>
                          <Typography sx={{ color: MUTED, fontSize: "0.95rem", lineHeight: 1.8, mb: 2 }}>
                            We sent a reset link to <strong>{email}</strong>.
                            Please check your inbox and spam folder. The link expires in 1 hour.
                          </Typography>
                          <Stack spacing={1.1}>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<ArrowBackIcon />}
                              onClick={() => navigate("/login")}
                              sx={{
                                py: 1.1,
                                borderRadius: "10px",
                                background: BRAND,
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: "0.98rem",
                                boxShadow: "0 10px 24px rgba(249,115,91,0.26)",
                                "&:hover": {
                                  background: BRAND_DARK,
                                  boxShadow: "0 12px 28px rgba(249,115,91,0.33)",
                                },
                              }}
                            >
                              Back to Login
                            </Button>
                            <Button
                              variant="outlined"
                              fullWidth
                              onClick={() => setSubmitted(false)}
                              sx={{
                                py: 1.05,
                                borderRadius: "10px",
                                borderColor: BORDER,
                                color: MUTED,
                                textTransform: "none",
                                fontWeight: 700,
                                "&:hover": {
                                  borderColor: "#B9C7D8",
                                  background: "#F8FAFC",
                                },
                              }}
                            >
                              Use another email
                            </Button>
                          </Stack>
                        </Box>
                      ) : (
                        <ValidatorForm
                          onSubmit={handleSubmit}
                          instantValidate={false}
                          autoComplete="off"
                        >
                          <TextValidator
                            label="Email Address"
                            onChange={(e) => setEmail(e.target.value)}
                            name="email"
                            type="email"
                            value={email}
                            fullWidth
                            variant="outlined"
                            validators={["required", "isEmail"]}
                            errorMessages={["Email is required", "Email is not valid"]}
                            autoFocus
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailOutlined sx={{ color: BRAND }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                                "& fieldset": { borderColor: "#D9E2EC" },
                                "&:hover fieldset": { borderColor: "#B9C7D8" },
                                "&.Mui-focused fieldset": { borderColor: BRAND },
                              },
                            }}
                          />

                          {error && (
                            <Box
                              sx={{
                                mt: 1.2,
                                mb: 1.2,
                                px: 1.2,
                                py: 0.9,
                                borderRadius: "9px",
                                background: "rgba(239,68,68,0.08)",
                                border: "1px solid rgba(239,68,68,0.2)",
                              }}
                            >
                              <Typography
                                color="error"
                                sx={{ fontWeight: 600, fontSize: "0.88rem" }}
                              >
                                {error}
                              </Typography>
                            </Box>
                          )}

                          <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                              mt: 1.8,
                              py: 1.1,
                              borderRadius: "10px",
                              background: BRAND,
                              textTransform: "none",
                              fontWeight: 700,
                              fontSize: "0.98rem",
                              boxShadow: "0 10px 24px rgba(249,115,91,0.26)",
                              "&:hover": {
                                background: BRAND_DARK,
                                boxShadow: "0 12px 28px rgba(249,115,91,0.33)",
                              },
                            }}
                          >
                            {loading ? "Sending..." : "Send Reset Link"}
                          </Button>

                          <Divider sx={{ my: 1.6, borderColor: BORDER }} />

                          <Box sx={{ textAlign: "center" }}>
                            <Typography sx={{ color: MUTED, fontSize: "0.86rem" }}>
                              Remember your password?{" "}
                              <Link
                                href="/login"
                                sx={{
                                  color: BRAND_DARK,
                                  fontWeight: 700,
                                  textDecoration: "none",
                                  borderBottom: "2px solid rgba(220,95,73,0.35)",
                                  "&:hover": { borderBottomColor: BRAND_DARK },
                                }}
                              >
                                Sign in
                              </Link>
                            </Typography>
                          </Box>
                        </ValidatorForm>
                      )}
                    </Box>
                  </CardContent>
                </Box>
              </Grid>
            </Grid>
          </MotionCard>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
